import { z } from 'zod'
import { jsonSchema } from '../helpers/json-schema.js'

/**
 * One marketing page as the editor sees it: the venue's own, or one track's.
 *
 * **`content` is the document as stored, not as validated**, which is the whole
 * point. A page that no longer satisfies the contract is reported rather than
 * dropped — the content API refuses to serve it saying it needs editing, and
 * this is the screen where that editing happens. Handing back a parsed value
 * would mean handing back nothing at all for exactly the pages that need
 * repairing, leaving somebody to retype twenty-five sections to fix one row.
 *
 * So the editor always receives what is really there, and `problems` says
 * whether it currently passes. Everything that *serves* this content —
 * `GET /content/...` — parses it first and refuses it if it does not.
 */
export const venueMarketingPageDtoSchema = z.object({
  id: z.string(),
  /** Null for the venue's own page; otherwise the track this one belongs to. */
  eventTrackId: z.string().nullable(),
  /** The stored document, whatever shape it is in. */
  content: jsonSchema,
  /** Null when the page satisfies the contract. Otherwise what a person has to fix. */
  problems: z.array(z.string()).nullable(),
})

export type VenueMarketingPageDto = z.infer<typeof venueMarketingPageDtoSchema>

/** Every page a venue has, as `GET /v1/venues/:venueId/marketing` returns them. */
export const venueMarketingPageListDtoSchema = z.array(venueMarketingPageDtoSchema)
